import React, { FunctionComponent } from "react";

import { UNKNOWN_KEY_TEXT } from "@multiweb/reactCore/i18n";
import { bemClassNames, createQA, Key } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { OrderType } from "@multiweb/types/multiweb";
import { Role } from "@multiweb/uiShared/Role";
import { Icon, IconColor } from "../Icon";
import { Layout, SpacingSize } from "../Layout";
import { Skeleton } from "../Skeleton";
import { FontWeight, Text, TextColor } from "../Text";

export type ListHeaderAttributeComponentProps = {
    /** Lokalizovaný název atributu. */
    attributeName?: string;
    /** Lokalizovaný popisek atributu. */
    attributeTitle?: string;
    /** Druh řazení. */
    orderType?: OrderType;
    /** Akce, která se provede při obyčejném kliku na atribut. */
    onClick: () => void;
    /** Akce, která se provede při shift kliku. */
    onShiftClick: () => void;
    /** Určuje, zda probíhá načítání lokalizací. */
    isLocalizationsFetching: boolean;
    /** Určuje, zda je komponenta vizuálně aktivní a zda s ní lze pracovat. */
    interactive?: boolean;
};

const classNames = bemClassNames("list-header-attribute-component");

export const ListHeaderAttributeComponent: FunctionComponent<ListHeaderAttributeComponentProps> = ({
    attributeName = UNKNOWN_KEY_TEXT,
    attributeTitle,
    orderType,
    onClick,
    onShiftClick,
    isLocalizationsFetching,
    interactive = true,
}) => (
    <span
        className={classNames()}
        onClick={(event) => {
            if (interactive) {
                event.shiftKey ? onShiftClick() : onClick();
            }
        }}
        {...createQA(attributeName)}
        role={Role.columnHeader}
        aria-sort={orderType ?? undefined}
    >
        {isLocalizationsFetching ? (
            <div className={classNames("skeleton")}>
                <Skeleton />
            </div>
        ) : (
            <Text
                value={attributeName}
                role={Role.button}
                title={attributeTitle}
                color={orderType ? undefined : TextColor.textSecondary}
                fontWeight={orderType && FontWeight.bold}
                ellipsis
                hideOutlineOnFocus
                onKeyDown={(event) => {
                    if (event.key === Key.Space || event.key === Key.Enter) {
                        event.preventDefault();
                        event.shiftKey ? onShiftClick() : onClick();
                    }
                }}
                disabled={!interactive}
            />
        )}
        <Layout spacingSizeRight={SpacingSize.xs} ariaHidden={true}>
            {orderType && !isLocalizationsFetching && (
                <Icon
                    iconCode={
                        orderType === OrderType.ascending ? IconCode.uiArrowTopSmall : IconCode.uiArrowBottomSmall
                    }
                    color={IconColor.textPrimary}
                    disabled={!interactive}
                />
            )}
        </Layout>
    </span>
);
